import { buttonUiTemplate } from "../../ConceptOne/src/frontViews/buttonUiTemplate";
import { OrientedButtonFrontView } from "../../ConceptOne/src/frontViews/ButtonFrontview";
import type { RockerRowConfig } from "../../ConceptOne/src/frontViews/GlasButtonFrontView";
import type { BaseButtonProps } from "../models/BaseDeviceProps";
import type Device from "../models/Device";
import type Button from "../models/Button";
import type Channel from "../models/Channel";
import { RockerConfig } from "../models/ButtonRockerConfig";

export function genericButtonRows(params: BaseButtonProps, device?: Device): RockerRowConfig[] {
  const display = (channel: Channel | undefined) => {
    const name = channel?.nameOrConnectedNames;
    return channel && name ? { channelType: channel.channelType, name } : undefined;
  };

  return Array.from({ length: params.numberOfButtonRows }, (_, index) => {
    const tag = `T${String(index + 1).padStart(2, "0")}`;

    const shared = device?.channels.getItemByTag(tag);
    const left = device?.channels.getItemByTag(`${tag}L`);
    const right = device?.channels.getItemByTag(`${tag}R`);

    const rocker = device?.isButton ? (device as Button).rockerConfigs?.[index + 1] : undefined;
    const split = params.supportsSplitRocker && rocker !== RockerConfig.rocker && (left || right || !shared);

    return split
      ? { mode: "split", left: display(left), right: display(right) }
      : { mode: "shared", shared: display(shared ?? left) };
  });
}

/** Read-only use of the same front view as the button editor. */
export default function GenericButtonPreview({ params, device }: { params: BaseButtonProps; device?: Device }) {
  if (!Number.isInteger(params.numberOfButtonRows) || params.numberOfButtonRows < 1) return null;
  const button = params;
  if (
    !Number.isFinite(button.widthMm) ||
    button.widthMm <= 0 ||
    !Number.isFinite(button.heightMm) ||
    button.heightMm <= 0
  )
    return null;
  const scale = 280 / Math.min(button.widthMm, button.heightMm);
  const width = button.widthMm * scale;
  const height = button.heightMm * scale;
  const rows = genericButtonRows(params, device);
  const color = button.color.trim().toLowerCase();

  const frontView = (
    <OrientedButtonFrontView
      uiTemplate={buttonUiTemplate(button.type)}
      rows={rows}
      width={width}
      height={height}
      // svg2pdf reads nested SVG dimensions from CSS; height: auto would become NaN.
      style={{ maxWidth: "none", height }}
      usePhysicalSize={false}
      color={color === "black" || color === "schwarz" || color === "#000000" || color === "#000" ? "black" : "white"}
      horizontal={button.horizontal}
      hasDisplay={button.hasDisplay}
      constrainLabels
    />
  );
  return frontView;
}
